import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'hooks/api/useTranslation';
import { useAppDispatch } from 'store/hooks';
import {
  useGetSubscriptionsQuery,
  userApiSlice,
  useUpdateStripeLanguageMutation,
} from 'store/api/userApiSlice';
import { StripeLanguageEnum } from 'store/api/apiTypes';
import { Modal } from 'shared/ui/modal/modal';
import { Card } from 'shared/ui/card/card';
import Button from 'shared/ui/button/button';
import { Icon } from 'shared/ui/icon/icon';
import { ICONS } from 'shared/constants/images';
import { WhatsIncludedDrawer } from '../whats-included-drawer/whats-included-drawer';
import { Filter } from 'shared/constants/color-filters';
import { IconTooltip } from 'shared/ui/icon-tooltip/icon-tooltip';
import { useGetOrderPreviewQuery } from 'store/api/apiSlice';
import { getCurrencySymbol } from 'shared/helpers/getCurrencySymbol';
import { Skeleton } from 'shared/ui/skeleton/skeleton';
import { pushDataLayerEvent } from 'shared/helpers/dataLayer';
import { getTrafficSource } from 'utils/trafficSource';
import cls from './buy-subscription-modal.module.scss';
import {
  BuySubscriptionModalProps,
  BuySubscriptionModalStep,
} from './buy-subscription-modal.types';
import { CurrentSpendBanner } from './current-spend-banner';
import { OrderSummary } from './order-summary';
import { Checkout } from './ui/checkout/checkout';

const PAYMENT_SUCCESS_HOLD_MS = 1600;

export const BuySubscriptionModal: FC<BuySubscriptionModalProps> = ({
  isOpen,
  onClose,
  quantity,
  onQuantityChange,
}) => {
  const { t } = useTranslation();
  const { currentLocale } = useTranslation();
  const [updateStripeLanguage] = useUpdateStripeLanguageMutation();

  const dispatch = useAppDispatch();
  const [step, setStep] = useState<BuySubscriptionModalStep>(
    BuySubscriptionModalStep.SUMMARY
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [paymentSucceeded, setPaymentSucceeded] = useState(false);

  const { data: licenses, isLoading: isLicensesLoading } =
    useGetSubscriptionsQuery(false);

  const haveBase = licenses ? licenses.count.total > 0 : false;

  const orderItems = [
    ...(haveBase ? [] : [{ product_id: 1, quantity: 1 }]),
    ...(haveBase || quantity !== 0
      ? [{ product_id: 2, quantity: Math.max(quantity, 1) }]
      : []),
  ];

  const {
    data: orderPreview,
    isFetching: isOrderPreviewFetching,
    isLoading: isOrderPreviewLoading,
    isError: isOrderPreviewError,
    refetch: refetchOrderPreview,
  } = useGetOrderPreviewQuery(orderItems, { skip: isLicensesLoading });

  const currencySymbol = getCurrencySymbol(orderPreview?.currency ?? 'USD');
  const coreLineItem = orderPreview?.line_items.find(
    item => item.product_id === 1
  );
  const addonLineItem = orderPreview?.line_items.find(
    item => item.product_id === 2
  );
  const subscriptionPrice = coreLineItem
    ? `${currencySymbol}${coreLineItem.amount.toFixed(0)}`
    : '49';

  const additionalPrice = addonLineItem
    ? `${currencySymbol}${addonLineItem.amount.toFixed(0)}`
    : '29';

  const onMinusClick = () => {
    onQuantityChange(quantity - 1);
  };
  const onPlusClick = () => {
    onQuantityChange(quantity + 1);
  };
  useEffect(() => {
    if (!haveBase) {
      onQuantityChange(0);
    }
  }, [haveBase, onQuantityChange]);

  const handleClose = () => {
    onClose();
    setStep(BuySubscriptionModalStep.SUMMARY);
    setPaymentSucceeded(false);
  };

  const handlePaymentSuccess = (sessionId?: string) => {
    pushDataLayerEvent('purchase', {
      transaction_id: sessionId,
      currency: orderPreview?.currency,
      value: orderPreview?.prorated_first_payment_incl_vat,
      trafficSource: getTrafficSource() ?? 'direct',
      items: (orderPreview?.line_items ?? []).map(lineItem => ({
        item_id:
          lineItem.product_id === 1
            ? 'Riva_License_Base'
            : 'Riva_License_Addon',
        quantity: lineItem.quantity,
        price: lineItem.amount,
      })),
    });
    dispatch(
      userApiSlice.util.invalidateTags(['Subscriptions', 'CurrentSpend'])
    );
    setPaymentSucceeded(true);
  };

  const handleProceed = async () => {
    const lang_id =
      currentLocale === 'de' ? StripeLanguageEnum.GE : StripeLanguageEnum.EN;
    try {
      await updateStripeLanguage({ lang_id }).unwrap();
    } catch {
      // proceed to checkout even if the language update fails
    }
    setStep(BuySubscriptionModalStep.CHECKOUT);
  };

  useEffect(() => {
    if (!paymentSucceeded) {
      return undefined;
    }
    const timer = setTimeout(handleClose, PAYMENT_SUCCESS_HOLD_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentSucceeded]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      variant='fullscreen'
      onBack={
        step === BuySubscriptionModalStep.CHECKOUT && !paymentSucceeded
          ? () => setStep(BuySubscriptionModalStep.SUMMARY)
          : undefined
      }
    >
      <div className={cls.modalContent}>
        {step === BuySubscriptionModalStep.SUMMARY ? (
          <>
            <Card className={cls.details}>
              <section className={cls.heading}>
                <h2>{t('buySubscriptions')}</h2>
                <p>{t('buySubscriptionsDescriptionStandalone')}</p>
              </section>
              <section className={cls.content}>
                <article>
                  <div className={cls.subscriptionDetails}>
                    <p>{t('rivaLicense')}</p>
                    <div className={cls.subscriptionPrice}>
                      {isOrderPreviewLoading && !isOrderPreviewError ? (
                        <Skeleton width={80} height={20} />
                      ) : (
                        subscriptionPrice && <p>{`${subscriptionPrice}/`}</p>
                      )}
                      <span>{t('month')}</span>
                    </div>
                  </div>
                  {isLicensesLoading ? (
                    <div>
                      <Skeleton height={40} />
                    </div>
                  ) : !haveBase ? (
                    <div className={cls.status}>
                      <span>{t('subscriptionsDescStdln')}</span>
                      <p className={cls.order}>
                        <Icon pathToIcon={ICONS.checkMarkGreen} size={16} />
                        {t('inOrder')}
                      </p>
                    </div>
                  ) : (
                    <div className={cls.have}>
                      <Icon pathToIcon={ICONS.checkCircle} size={20} />
                      <p>{t('haveBase')}</p>
                    </div>
                  )}
                </article>
                {!isLicensesLoading && !haveBase && (
                  <section className={cls.whatsIncluded}>
                    <div className={cls.plan}>
                      <div className={cls.feature}>
                        <div className={cls.iconWrapper}>
                          <Icon
                            pathToIcon={ICONS.checkMarkNeutral}
                            size={14}
                            filter={Filter.onSurfaceVariant}
                          />
                        </div>
                        <p>{t('coversOneWebsite')}</p>
                      </div>
                      <div className={cls.feature}>
                        <div className={cls.iconWrapper}>
                          <Icon
                            pathToIcon={ICONS.checkMarkNeutral}
                            size={14}
                            filter={Filter.onSurfaceVariant}
                          />
                        </div>
                        <p>{t('threeOfferings')}</p>
                        <IconTooltip
                          id='plan-offerings-tooltip'
                          iconSize={16}
                          wrapperClassName={cls.tooltip}
                          variant='light'
                          tooltipClassName={cls.whiteTooltip}
                          noArrow
                          opacity={1}
                          maxWidth={'250px'}
                          zIndex={1001}
                        >
                          <span>
                            <b>{t('offering')}</b>
                          </span>
                          <br />
                          <span>{t('offeringTooltip')}</span>
                        </IconTooltip>
                      </div>
                      <div className={cls.feature}>
                        <div className={cls.iconWrapper}>
                          <Icon
                            pathToIcon={ICONS.checkMarkNeutral}
                            size={14}
                            filter={Filter.onSurfaceVariant}
                          />
                        </div>
                        <p>{t('tenHumanLikeQueries')}</p>
                        <IconTooltip
                          id='plan-human-like-query-tooltip'
                          iconSize={16}
                          wrapperClassName={cls.tooltip}
                          variant='light'
                          tooltipClassName={cls.whiteTooltip}
                          noArrow
                          opacity={1}
                          maxWidth={'250px'}
                          zIndex={1001}
                        >
                          <span>
                            <b>{t('humanLikeQTooltipHeading')}</b>
                          </span>
                          <br />
                          <span>{t('humanLikeQTooltip')}</span>
                        </IconTooltip>
                      </div>
                      <div className={cls.feature}>
                        <div className={cls.iconWrapper}>
                          <Icon
                            pathToIcon={ICONS.checkMarkNeutral}
                            size={14}
                            filter={Filter.onSurfaceVariant}
                          />
                        </div>
                        <p>{t('reportsEveryFifteen')}</p>
                      </div>
                    </div>
                    <Button
                      theme='text'
                      tone='default'
                      className={cls.whatsIncludedBtn}
                      onClick={() => setIsDrawerOpen(true)}
                    >
                      {t('whatsIncluded')}
                    </Button>
                  </section>
                )}
                <div className={cls.quantity}>
                  <div className={cls.addon}>
                    <h3>
                      {t('additional')} {t('rivaLicense')}
                    </h3>
                    <p>{t('addonDesc')}</p>
                  </div>
                  <div>
                    <div className={cls.subscriptionPrice}>
                      {isOrderPreviewLoading && !isOrderPreviewError ? (
                        <Skeleton width={80} height={20} />
                      ) : (
                        additionalPrice && <p>{`${additionalPrice}/`}</p>
                      )}
                      <span>{t('month')}</span>
                    </div>
                    <div className={cls.quantityManage}>
                      <Button
                        onClick={onMinusClick}
                        disabled={quantity === 0}
                        className={cls.minusBtn}
                      >
                        －
                      </Button>
                      <p>{quantity}</p>
                      <Button onClick={onPlusClick} className={cls.plusBtn}>
                        ＋
                      </Button>
                    </div>
                  </div>
                </div>
              </section>
            </Card>
            <Card className={cls.summaryCard}>
              <OrderSummary
                quantity={quantity}
                preview={orderPreview}
                isLoading={isOrderPreviewFetching}
                isError={isOrderPreviewError}
                haveBase={haveBase}
                onRetry={refetchOrderPreview}
                onProceed={handleProceed}
              />
            </Card>
          </>
        ) : (
          <Card className={cls.stepCard}>
            {paymentSucceeded ? (
              <div className={cls.paymentSuccess}>
                <Icon pathToIcon={ICONS.checkCircle} size={48} />
                <p className={cls.paymentSuccessTitle}>
                  {t('buySubscriptionModalPaymentSuccessTitle')}
                </p>
                <p className={cls.paymentSuccessSubtitle}>
                  {t('buySubscriptionModalPaymentSuccessSubtitle')}
                </p>
              </div>
            ) : (
              <Checkout items={orderItems} onSuccess={handlePaymentSuccess} />
            )}
          </Card>
        )}
      </div>

      {step === BuySubscriptionModalStep.SUMMARY && <CurrentSpendBanner />}

      <WhatsIncludedDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </Modal>
  );
};
